import { ArrowForward } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';

import CFactsheetDisclaimer from '../../components/CFactsheetDisclaimer';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CStatefulContainer from '../../components/CStatefulContainer';
import ImageWrapper from '../../components/ImageWrapper';
import MagicCarouselArticleCard from '../../components/MagicCarouselArticleCard';
import GhostPostGalleryLinkButton from '../../components/dashboardContent/GhostPostGalleryLinkButton';
import Page from '../../components/layout/Page';
import XDResponsiveGridWrapper from '../../components/layout/XDResponsiveGridWrapper';
import { BreakpointsInPx } from '../../enums/BreakpointsInPx';
import { PageRoutes } from '../../enums/enums';
import { useAppSelector } from '../../hooks/hooks';
import type { GhostTags } from '../../models/GhostPostModel';
import { type GhostPostSummaryItemModel, type GhostPostSummaryList } from '../../models/GhostPostSummaryListModel';
import type { IRequestState } from '../../models/IRequestState';
import { SvNewsService } from '../../services/SvNewsService';
import CMagneticSnapCarousel from './CMagneticSnapCarousel';

interface State {
  featuredPosts?: GhostPostSummaryList;
  featuredPostsState: IRequestState;
  recentPosts?: GhostPostSummaryList;
  recentPostsState: IRequestState;
  tags?: GhostTags;
}

export default function NewsIndex() {
  const { dashboard } = useAppSelector((state) => state);
  const [state, setState] = useState<State>({
    featuredPostsState: { isLoading: true, isError: false },
    recentPostsState: { isLoading: true, isError: false }
  });
  const updateState = (obj: Partial<State>) => {
    setState((state) => ({ ...state, ...obj }));
  };
  const { featuredPostsState } = state;

  const fetchPosts = async () => {
    updateState({
      featuredPostsState: { ...featuredPostsState, isLoading: true },
      recentPostsState: { ...featuredPostsState, isLoading: true }
    });
    try {
      const featuredPosts = await SvNewsService.getAllPosts('1', 'hash-feature', 'newest');
      const chronologicalPosts = await SvNewsService.getAllPosts('1', '', 'newest');
      updateState({
        featuredPosts: await featuredPosts.getData(),
        featuredPostsState: { isLoading: false, isError: false },
        recentPosts: await chronologicalPosts.getData(),
        recentPostsState: { isLoading: false, isError: false }
      });
    } catch (e) {
      updateState({ featuredPostsState: { isLoading: false, isError: true } });
    }
  };

  useEffect(() => {
    (async () => {
      await fetchPosts();
    })();
  }, []);

  const featuredPostsLabel = CLocalizedTextStringStatic('news.home.featured.heading');
  const latestPostsLabel = CLocalizedTextStringStatic('news.home.latestArticles.heading');

  const shouldRenderDisclaimer =
    !state.recentPostsState.isLoading && !state.recentPostsState.isError && (state.recentPosts?.posts?.length ?? 0) > 0;

  return (
    <Page>
      {(state.featuredPosts?.posts?.length ?? 0) > 0 && (
        <CPageModule fullWidth>
          <CPageModule submodule>
            <h2>{featuredPostsLabel}</h2>
          </CPageModule>
          <CPageModule fullWidth submodule style={{ paddingTop: 0 }}>
            <CMagneticSnapCarousel
              interval={15}
              viewportPadding={{
                paddingLeft: dashboard.dashboardOutletDimensions.padding,
                paddingRight: dashboard.dashboardOutletDimensions.padding
              }}
            >
              {state.featuredPosts?.posts.map((e) => (
                <MagicCarouselArticleCard key={e.slug} post={e} width={dashboard.dashboardOutletDimensions.columnW} />
              ))}
            </CMagneticSnapCarousel>
          </CPageModule>
        </CPageModule>
      )}
      <CPageModule>
        {((state.recentPosts?.posts?.length ?? 0) > 0 ||
          (!state.recentPostsState.isLoading && !state.recentPostsState.isError)) && <h2>{latestPostsLabel}</h2>}
        <CStatefulContainer apiRequestState={state.recentPostsState} showContent={!state.recentPostsState.isLoading}>
          <XDResponsiveGridWrapper>
            {state.recentPosts?.posts
              .slice(
                0,
                dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_4
                  ? 3
                  : dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_3
                    ? 2
                    : dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_2
                      ? 3
                      : 3
              )
              .map((e) => (
                <GhostPostGalleryLinkButton
                  post={e}
                  pathPrefix={`../../${PageRoutes.NEWS_OUTLET}/${PageRoutes.NEWS_ARTICLES_OUTLET}`}
                  key={e.slug}
                />
              ))}
            <ViewRemainingButton
              recentPosts={state.recentPosts?.posts.slice(
                dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_4
                  ? 3
                  : dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_3
                    ? 2
                    : dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_2
                      ? 3
                      : 3,
                (dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_4
                  ? 3
                  : dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_3
                    ? 2
                    : dashboard.dashboardOutletDimensions.maxPageModuleW > BreakpointsInPx.BREAKPOINT_2
                      ? 3
                      : 3) + 3
              )}
            />
          </XDResponsiveGridWrapper>
        </CStatefulContainer>
      </CPageModule>
      {shouldRenderDisclaimer && <CFactsheetDisclaimer />}
    </Page>
  );
}

function ViewRemainingButton(props: { recentPosts?: GhostPostSummaryItemModel[] }) {
  const viewAllPostsLabel = CLocalizedTextStringStatic('news.home.latestArticles.viewRemaining');

  return (
    <GhostPostGalleryLinkButton
      post={{
        featureImage: (
          <div className={'h-full relative z-0'}>
            {props.recentPosts?.map((e, ix) => (
              <div
                key={e.slug}
                className={'absolute w-full flex'}
                style={{
                  top: ix * 5,
                  justifyContent: ix === 0 ? 'start' : ix === 1 ? 'center' : 'end',
                  // left: `calc(${(ix / 3) * 100}% - (${ix === 0 ? 0 : ix * 25}px)`,
                  zIndex: -1 * ix,
                  height: `calc(100% - ${ix * 10}px)`
                }}
              >
                <div className={'aspect-square h-full rounded-xl border-[6px] border-white overflow-hidden bg-white'}>
                  <ImageWrapper alt={e.title ?? ''} src={e.featureImage} className={'w-full h-full'} />
                </div>
              </div>
            ))}
          </div>
        ),
        title: (
          <>
            {viewAllPostsLabel} <ArrowForward />
          </>
        ),
        id: 'all-articles',
        slug: '.',
        excerpt: '',
        url: '',
        authors: [],
        readingTime: 0,
        disclaimerWall: false,
        tags: []
      }}
      pathPrefix={`../../${PageRoutes.NEWS_OUTLET}/${PageRoutes.NEWS_ARTICLES_OUTLET}`}
    />
  );
}
